<template>
  <b-card no-body>
    <b-col class="d-flex justify-content-between px-2 py-1">
      <b-button
        v-if="accountAccessManager.canManageUsers()"
        variant="primary"
        :disabled="loading"
        size="sm"
        @click="openAddUserModal"
      >
        <GjIcon
          class="mr-50"
          name="Plus"
        />
        {{ $tk('ClientPortal.Account.Edit.AddUser') }}
      </b-button>

      <SearchInputField
        class="mb-0"
        :disabled="loading"
        :placeholder="$tk('ClientPortal.Form.Search')"
        @onInputSearch="onSearchChange"
      />
    </b-col>
    <b-skeleton-table
      v-if="loading"
      :columns="4"
      :rows="10"
    />
    <div class="client-table--account">
      <b-table
        v-if="!loading"
        :items="items"
        :fields="fields"
        :filter="filter"
        class="client-table__data users-table"
        show-empty
        :empty-text="$tk('ClientPortal.Form.NoUserHaveBeenCreatedYet')"
        :current-page="currentPage"
        :per-page="perPage"
        responsive
        :tbody-tr-class="addAnimation"
      >
        <template #cell(fullName)="data">
          <b-avatar
            :src="data.item.imageUrl"
            variant="white"
            class="mr-50"
          />
          <span>
            {{ data.item.fullName }}
          </span>
        </template>
        <template #cell(status)="data">
          <span class="text-success">{{ data.item.status }}</span>
        </template>
        <template #empty="scope">
          <div class="text-center p-3 client-table__empty">
            <div class="empty__client-table">
              <GjIcon
                size="64px"
                name="AvatarAdd"
              />
              <div class="mb-50">
                {{ scope.emptyText }}
              </div>
              <b-link
                @click="openAddUserModal"
              >
                <GjIcon
                  class="mr-50"
                  name="Plus"
                /> {{ $tk('ClientPortal.Account.Edit.AddUser') }}
              </b-link>
            </div>
          </div>
        </template>
        <template #cell(role)="data">
          <div class="d-flex align-items-center justify-content-between">
            <span>
              {{ data.item.role }}
            </span>
            <b-dropdown
              v-if="data.item.editable && accountAccessManager.canManageUsers()"
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              button-class="p-0"
              right
            >
              <template v-slot:button-content>
                <GjIcon name="Moreoptions_fill" />
              </template>
              <b-dropdown-item @click="openEditUserModal(data.item, data.index)">
                <GjIcon
                  class="mr-50"
                  name="Edit"
                />
                {{ $tk('ClientPortal.Common.Edit') }}
              </b-dropdown-item>
              <b-dropdown-item @click="openDeleteUserModal(data.item, data.index)">
                <GjIcon
                  class="mr-50"
                  name="Delete"
                />
                {{ $tk('ClientPortal.Common.Delete') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>
    </div>
    <b-card-footer
      v-if="items.length > 0"
      class="client-table__footer d-flex"
    >
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        size="sm"
        class="my-0 mr-1"
        hide-goto-end-buttons
      >
        <template #prev-text>
          <GjIcon
            class="mr-25"
            name="ArrowLeft"
          />
        </template>
        <template #next-text>
          <GjIcon
            class="ml-25"
            name="ArrowRight"
          />
        </template>
      </b-pagination>
      <span>
        {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }}
        {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }}
        {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }}
        {{ $tk('ClientPortal.Pagination.Items') }}
      </span>
    </b-card-footer>
    <b-modal
      id="user-remove-modal"
      ref="removeUserModal"
      :title="$tk('ClientPortal.Account.Edit.RemoveUser')"
      centered
      footer="footer"
    >
      <span>{{ $tk('ClientPortal.Account.Edit.AreYouSureYouWantToRemoveUserAccount') }}</span>
      <template #modal-footer="{ cancel }">
        <b-button
          class="m-0"
          size="sm"
          variant="outline-secondary"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="m-0 ml-1 float-right"
          :disabled="saving"
          @click="confirmRemove()"
        >
          {{ $tk('ClientPortal.Form.Actions.Delete') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal
      id="modal-add-user"
      ref="addUserModal"
      :title="userModal.user.id > 0 ? $tk('ClientPortal.Account.Edit.EditUser') : $tk('ClientPortal.Account.Edit.AddNewUser')"
      centered
      footer="footer"
    >
      <b-form-group
        class="mb-2"
        :label="$tk('ClientPortal.Account.Edit.Email') + ' *'"
        :invalid-feedback="errors['Email']"
      >
        <b-form-input
          v-model="userModal.user.email"
          type="email"
          placeholder="example@email.com"
          :disabled="userModal.user.id > 0"
          :state="errors['Email'] == null ? null : false"
        />
      </b-form-group>
      <b-form-group
        :label="$tk('ClientPortal.Account.Edit.Role') + ' *'"
        class="mb-0"
      >
        <div
          v-for="role in userRoles"
          :key="role.id"
        >
          <b-form-radio
            v-if="role.editable"
            v-model.number="userRole"
            class="mb-1"
            :value="role.id"
          >
            <div class="text-dark mb-50">
              {{ role.name }}
            </div>
            <span class="text-secondary">
              {{ role.description }}
            </span>
          </b-form-radio>
        </div>
      </b-form-group>
      <template #modal-footer="{ cancel }">
        <b-button
          class="m-0"
          size="sm"
          variant="outline-secondary"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          class="m-0 ml-1 float-right"
          :disabled="saving || !isFormComplete"
          @click="userModal.user.id > 0 ? updateAccountUser(userModal.user, userModal.index) : createAccountUser(userModal.user)"
        >
          {{ $tk('ClientPortal.Form.Actions.Save') }}
        </b-button>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import AccountsService from '@/api/client/accounts.service'
import notification from '@/mixins/toastification'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import { AccountAccessManager } from '@/utils/accessManager/accessManager'
import SearchInputField from '@/components/SearchInputField.vue'

export default {
  components: {
    GjIcon,
    SearchInputField,
  },
  mixins: [notification],
  data() {
    return {
      userModal: {
        user: {},
        index: 0,
      },
      userRoles: [],
      errors: {},
      loading: true,
      saving: false,
      items: [],
      currentPage: 1,
      perPage: 10,
      filter: null,
      removeItem: [],
      removeIndex: 0,
      accountAccessManager: AccountAccessManager,
      userRole: null,
      addedRowId: null,
    }
  },
  computed: {
    fromCount() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    toCount() {
      const toCount = this.fromCount + this.perPage - 1
      return Math.min(toCount, this.totalRows)
    },
    isFormComplete() {
      const { email } = this.userModal.user

      return email && this.userRole
    },
    totalRows() {
      return this.items.length
    },
    fields() {
      return [
        {
          key: 'fullName',
          label: this.$tk('ClientPortal.Account.Create.Name'),
          sortable: true,
        },
        {
          key: 'email',
          label: this.$tk('ClientPortal.Account.Create.Email'),
          sortable: true,
        },
        {
          key: 'role',
          label: this.$tk('ClientPortal.Account.Edit.Role'),
          sortable: true,
        },
      ]
    },
  },
  watch: {
    '$route.params.accountId': 'reloadAccountUsers',
  },
  async mounted() {
    this.loading = true
    const { accountId } = this.$route.params
    try {
      this.items = await AccountsService.getAccountUsers(accountId)
      this.userRoles = await AccountsService.getUserRoles(accountId)
    } catch (err) {
      this.error(err.message)
    } finally {
      this.loading = false
    }
  },
  methods: {
    onSearchChange(value) {
      this.filter = value
    },
    addAnimation(item) {
      const el = Array.from(document.querySelectorAll('.users-table > table > tbody > tr:nth-child(2)')).pop()
      if (item.id === this.addedRowId) {
        el.classList.remove('table-row-animation')
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight // trigger reflow
        el.classList.add('table-row-animation')
        this.addedRowId = null
      }
    },
    async reloadAccountUsers() {
      this.loading = true
      const { accountId } = this.$route.params
      try {
        this.items = await AccountsService.getAccountUsers(accountId)
        this.userRoles = await AccountsService.getUserRoles(accountId)
      } catch (err) {
        this.error(err.message)
      } finally {
        this.loading = false
      }
    },
    async createAccountUser(user) {
      this.saving = true
      this.errors = {}

      try {
        user.roleId = this.userRole
        const response = await AccountsService.createAccountUser(user, this.$route.params.accountId)
        if (response.saved) {
          this.addedRowId = response.userModel.id
          this.items.splice(1, 0, response.userModel)
          this.success(response.message)
          this.$refs.addUserModal.hide()
        } else if (response.invited) {
          this.success(response.message)
          this.$refs.addUserModal.hide()
        } else {
          this.error(response.message)
          this.errors = response.errors
        }
      } catch (err) {
        this.error(err.message)
      } finally {
        this.saving = false
      }
    },
    async updateAccountUser(user, index) {
      this.saving = true
      this.errors = {}
      try {
        user.roleId = this.userRole
        const response = await AccountsService.updateAccountUser(user, this.$route.params.accountId)

        if (response.saved) {
          this.items.splice(index, 1, response.userModel)
          this.success(response.message)
          this.$refs.addUserModal.hide()
        } else {
          this.error(response.message)
          this.errors = response.errors
        }
      } catch (err) {
        this.error(err.message)
      } finally {
        this.saving = false
      }
    },
    openAddUserModal() {
      this.errors = {}
      this.userModal = {
        user: {
          accountId: this.$route.params.accountId,
          email: '',
          roleId: 0,
        },
        index: -1,
      }
      this.userRole = 0
      this.$refs.addUserModal.show()
    },
    openEditUserModal(user, index) {
      this.errors = {}
      this.userModal.user = user
      this.userRole = user.roleId
      this.userModal.index = index
      this.$refs.addUserModal.show()
    },
    openDeleteUserModal(item, index) {
      this.removeItem = item
      this.removeIndex = index
      this.$refs.removeUserModal.show()
    },
    async confirmRemove() {
      try {
        const response = await AccountsService.removeUser(this.removeItem, this.$route.params.accountId)
        if (response.saved) {
          this.items.splice(this.removeIndex, 1)
          this.$refs.removeUserModal.hide()
          this.success(this.$tk('ClientPortal.Account.ConfirmRemove.UserRemovedSuccessfully'))
        }
      } catch (error) {
        this.error(error.message)
      }
    },
  },
}
</script>
<style lang="scss">
@import '@/assets/scss/views/added_row_animation.scss';
</style>
